import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, ExternalLink } from "lucide-react";
import { getProducts } from "@/lib/api";
import { STORE } from "@/lib/store";
import { useSEO } from "@/lib/seo";
import PageHeader from "@/components/PageHeader";
import ProductCard from "@/components/shop/ProductCard";
import { StaggerGroup, StaggerItem } from "@/components/motion/Reveal";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";

  const { data, isFetching } = useQuery({
    queryKey: ["search", q],
    queryFn: () => getProducts({ q }),
    enabled: q.trim().length > 0,
  });
  const products = data?.products || [];

  useSEO({
    title: q ? `Search: ${q} | Renova Peptide Co.` : "Search | Renova Peptide Co.",
    description: "Search research materials across the Renova Peptide Co. catalogue.",
    path: "/search",
    noindex: true,
  });

  return (
    <>
      <PageHeader
        overline="Search"
        title="Search the Catalogue"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Search", path: "/search" }]}
      />
      <section className="container-rnv py-16 md:py-20">
        <div className="relative max-w-xl">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#85827D]" />
          <input
            type="search"
            autoFocus
            value={q}
            onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {}, { replace: true })}
            placeholder="Search research materials…"
            aria-label="Search products"
            className="w-full bg-white border border-[#1E2A44]/12 rounded-sm pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-[#C8A96A]"
            data-testid="search-input"
          />
        </div>

        <div className="mt-10">
          {q.trim().length === 0 ? (
            <p className="text-sm text-[#85827D]">Enter a term above to search the catalogue.</p>
          ) : isFetching ? (
            <p className="text-sm text-[#85827D]">Searching…</p>
          ) : products.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-[#1E2A44]/15 rounded-sm" data-testid="search-empty">
              <p className="font-serif text-2xl text-[#1E2A44]">No results for “{q}”</p>
              <p className="mt-2 text-sm text-[#85827D]">Try a different term, or search the full store.</p>
              <a href={`${STORE.search}?q=${encodeURIComponent(q)}`} className="btn-gold mt-6">
                Search the store <ExternalLink size={14} />
              </a>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#85827D] mb-6" data-testid="search-count">{products.length} result{products.length !== 1 ? "s" : ""}</p>
              <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((p) => <StaggerItem key={p.slug}><ProductCard product={p} /></StaggerItem>)}
              </StaggerGroup>
            </>
          )}
        </div>
      </section>
    </>
  );
}
